/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { When } from "react-if";
import { theme } from "@nordcloud/gnui";
import { StandalonePagination, TableLoader, UniversalWrap } from "~/components";
import { EnvironmentDetails } from "~/components/ApplicationItem";
import { useApplicationEnvironments } from "../hooks";

type Props = {
  environmentsCount?: number;
  applicationId: string;
  showAppCo2eSavings: boolean;
};

export function EnvironmentList({
  applicationId,
  showAppCo2eSavings,
  environmentsCount,
}: Props) {
  const [paginationParams, setPaginationParams] = useState({
    page: 0,
    limit: PAGINATION_LIMIT,
  });
  const { environments, loading, error, count } = useApplicationEnvironments({
    applicationId,
    ...paginationParams,
  });

  return (
    <>
      <UniversalWrap
        errorProps={{ error }}
        loaderProps={{
          loading,
          items: environmentsCount,
          limit: paginationParams.limit,
          Component: Loader,
        }}
      >
        <EnvironmentDetails
          showOrganizationUnit
          applicationId={applicationId}
          environments={environments}
          showAppCo2eSavings={showAppCo2eSavings}
        />
      </UniversalWrap>
      <When condition={count > PAGINATION_LIMIT}>
        <StandalonePagination
          count={count}
          paginationParams={paginationParams}
          onPaginationChange={setPaginationParams}
        />
      </When>
    </>
  );
}

const PAGINATION_LIMIT = 10;
const DEFAULT_ENVIRONMENTS_COUNT = 3;

type LoaderProps = {
  items: number | undefined;
  limit: number;
};

function Loader({ items = DEFAULT_ENVIRONMENTS_COUNT, limit }: LoaderProps) {
  return (
    <TableLoader
      gapY={10}
      gapX={6}
      css={{
        marginTop: theme.spacing.spacing04,
      }}
      rows={[
        {
          count: Math.min(items, limit) + 1,
          height: 35,
          items: [
            {
              widthPercent: 70,
            },
            {
              widthPercent: 10,
            },
            {
              widthPercent: 10,
            },
            {
              widthPercent: 10,
            },
          ],
        },
      ]}
    />
  );
}
